<script lang="ts" setup>
import { useRoute, useRouter } from "vue-router";
import { computed, onMounted, ref, watch } from "vue";
import { usePreviewImg } from "@/use/common";
import {
  Image,
  Popup,
  showLoadingToast,
  showSuccessToast,
  Swipe,
  SwipeInstance,
  SwipeItem,
} from "vant";
import { MateTraitAnimation } from "@/types/entity";
import { PortraitGalleryInstance } from "@/components/mobile/media/PortraitGallery.vue";
import {
  useAnimationPortraitTypeText,
  useAnimationTypeText,
  useTraitAnimation,
} from "@/stores/traitAnimation";
import { TraitAnimationPortraitType, TraitAnimationType } from "@/enums";
import { durationGetter } from "./usePerformAnimation";
import i18n from "@/locales";

const getDefaultFrameState = (type: TraitAnimationType) => {
  switch (type) {
    case TraitAnimationType.Blink:
      return [
        { type: TraitAnimationPortraitType.EyesOpen },
        { type: TraitAnimationPortraitType.EyesClosed },
      ];
    case TraitAnimationType.LipSync:
    default:
      return [
        { type: TraitAnimationPortraitType.MouthOpen },
        { type: TraitAnimationPortraitType.MouthClosed },
        { type: TraitAnimationPortraitType.HalfOpenMouth },
      ];
  }
};

// States
const route = useRoute();
const router = useRouter();
const type: TraitAnimationType = Number(route.query.type);
const id = Number(route.query.id);
const traitId = ref(Number(route.query.traitId));

const loading = ref(false);
const traitAnimationStore = useTraitAnimation();
const swipeRef = ref<SwipeInstance | null>(null);
const animation = ref<MateTraitAnimation | null>(null);
const frames = computed({
  get: () => animation.value?.portraitzz ?? [],
  set: (value) => {
    if (animation.value) {
      animation.value.portraitzz = value;
    }
  },
});
const enableSubmit = computed(() =>
  frames.value.every((item) => item.portrait?.mediaId)
);

const previewIdx = ref(0);
const showMediaSelect = ref(false);
const portraitGalleryRef = ref<PortraitGalleryInstance | null>(null);
const portraitUrls = computed(() => {
  return frames.value
    .filter((item) => item.portrait)
    .map((item) => item.portrait.media!.url!);
});

// Lifecycle
onMounted(async () => {
  loading.value = true;
  try {
    if (id) {
      animation.value = await traitAnimationStore.getOrFetchTraitAnimationById(
        id
      );
    } else {
      animation.value = {
        mateTraitId: traitId.value,
        type,
        name: useAnimationTypeText(type),
        portraitzz: getDefaultFrameState(type),
      } as MateTraitAnimation;
    }
  } finally {
    loading.value = false;
  }
});

// Lifecycle
watch(showMediaSelect, () => {
  if (showMediaSelect.value) {
    portraitGalleryRef.value?.clear();
  }
});

// Methods
function onPreviewIdxChange(idx: number) {
  previewIdx.value = idx;
  swipeRef.value?.swipeTo(previewIdx.value, { immediate: true });
}

const onMediaSelect = () => {
  frames.value[previewIdx.value].portrait =
    portraitGalleryRef.value!.selectedPortraitList[0];
  showMediaSelect.value = false;
};

const onChangeName = () => {
  const name = window.prompt(
    i18n.global.t("Message.enterAnimationName"),
    animation.value!.name
  );
  if (name) {
    animation.value!.name = name;
  }
};

const onConfirm = async () => {
  const loading = showLoadingToast(i18n.global.t("Message.saving"));
  try {
    if (!id) {
      animation.value = await traitAnimationStore.createTraitAnimation(
        animation.value!
      );
    } else {
      animation.value = await traitAnimationStore.updateTraitAnimation(
        animation.value!.id,
        animation.value!
      );
    }
    loading.close();
    showSuccessToast(i18n.global.t("Message.saveSuccess"));
    router.back();
  } finally {
    loading.close();
  }
};
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr_auto]">
    <threads-nav show-back>
      <template #center>{{ useAnimationTypeText(type) }}</template>
      <template #right>
        <button
          :disabled="!enableSubmit"
          class="btn btn-primary btn-sm rounded"
          @click="onConfirm"
        >
          {{ $t("Buttons.sure") }}
        </button>
      </template>
    </threads-nav>
    <div class="w-full h-full flex flex-col gap-4 overflow-hidden">
      <div
        v-if="loading || !animation"
        class="w-full h-full flex items-center justify-center"
      >
        <span class="loading"></span>
      </div>
      <div v-else class="flex flex-col gap-1">
        <div class="flex">
          <div
            class="flex-1 w-full aspect-[3/4] relative overflow-hidden border-r border-y"
          >
            <swipe
              ref="swipeRef"
              :loop="false"
              class="w-full h-full bg-base-200"
              indicator-color="white"
              @change="(idx: number) => (previewIdx = idx)"
            >
              <swipe-item
                v-for="(item, idx) in frames"
                :key="idx"
                class="relative"
              >
                <Image
                  v-if="item.portrait"
                  :src="usePreviewImg(item.portrait!.media!.url!)"
                  class="w-full h-full pointer-events-none"
                  fit="scale-down"
                  crossorigin="anonymous"
                />
                <div
                  v-else
                  class="flex flex-col items-center justify-center w-full h-full gap-2"
                >
                  {{ $t("Animation.notSet") }}
                </div>
              </swipe-item>
              <template #indicator>
                <div
                  class="absolute left-2 bottom-2 bg-white/10 px-2 py-1 rounded text-xs"
                >
                  {{ useAnimationPortraitTypeText(frames[previewIdx].type) }}
                  {{ $t("Animation.verticalPainting") }}
                </div>
              </template>
            </swipe>
            <button
              class="btn btn-xs absolute right-2 bottom-2 btn-primary rounded"
              @click="showMediaSelect = true"
            >
              {{ $t("Menus.settings") }}
            </button>
          </div>
          <div class="relative flex-1 bg-base-200 border-y bx-0">
            <animation-stage
              :duration-getter="(index) => durationGetter(index, type)"
              :urls="portraitUrls"
              class="w-full h-full"
            />
            <div
              class="absolute left-2 bottom-2 bg-white/10 px-2 py-1 rounded text-xs"
            >
              {{ $t("Animation.preview") }}
            </div>
          </div>
        </div>
        <div class="flex justify-between items-center relative">
          <div class="tabs tabs-lifted w-full" role="tablist">
            <span
              class="tab tab-active [--tab-border-color:hsl(0,0%,18%)] w-fit"
              role="tab"
            >
              {{ $t("Animation.keyframe") }}
            </span>
            <div class="w-full overflow-x-auto rounded-bl rounded-br border">
              <div class="flex h-full">
                <div
                  v-for="(item, idx) in frames"
                  :key="idx"
                  :class="{
                    'border-primary': idx === previewIdx,
                    'border-transparent': idx !== previewIdx && item.portrait,
                    '': idx !== previewIdx && !item.portrait,
                  }"
                  class="h-32 border shrink-0 w-auto aspect-[3/4]"
                  @click="onPreviewIdxChange(idx)"
                >
                  <img
                    v-if="item.portrait"
                    :src="usePreviewImg(item.portrait.media?.url!)"
                    alt="preview"
                  />
                  <div
                    v-else
                    class="flex flex-col items-center justify-center w-full h-full gap-2 bg-base-200"
                  >
                    {{ useAnimationPortraitTypeText(item.type) }}
                    {{ $t("Animation.verticalPainting") }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="animation?.name"
            class="absolute flex rounded border right-1 top-0 overflow-hidden cursor-pointer"
            @click="onChangeName"
          >
            <span class="p-1 text-base-content/50">{{
              $t("Animation.name")
            }}</span>
            <span class="p-1 flex gap-1 items-center"
              >{{ animation!.name }} <edit-icon class="icon-sm"
            /></span>
          </div>
        </div>
      </div>
      <popup
        v-model:show="showMediaSelect"
        class="h-[80%] overflow-hidden"
        position="bottom"
        round
        teleport="body"
      >
        <portrait-gallery
          ref="portraitGalleryRef"
          :enable-mate-change="false"
          :filter="1"
          :max-select-count="1"
          :trait-id="traitId"
          selectable
          @back="showMediaSelect = false"
          @on-confirm="onMediaSelect"
        />
      </popup>
    </div>
  </div>
</template>
